// Single source of truth for "how many paid hours does member X get for incident
// Y?" (#1458). This rule used to be written four incompatible ways — the payroll
// api, the payroll drawer, the member timeline and the member-stats trigger —
// which is why a standby member showed 2h on the payroll grid and 0h on every
// other screen. Every reader routes through here so they can't drift apart.
//
// The two settings are INDEPENDENT. One is not a floor on the other:
//
//   incidentMinimumHours — the least a member who RESPONDED is paid, however
//     short the call. A floor on the incident's computed duration.
//   incidentStandbyHours — a flat credit for a member placed on STANDBY, used
//     *instead of* the duration. The minimum never touches it.
//
// A member is one or the other, never both. (The IAR import can put a uid in
// both arrays — it de-dupes each list only against itself — so this has to be
// settled somewhere: responder wins, counted once.)

// The credit a standby member gets when the tenant hasn't set one. Matches the
// long-standing api fallback so migrating call sites changes no existing pay.
const DEFAULT_STANDBY_HOURS = 2;

// Firestore stores these as numbers, but hand-edited docs and form values can
// arrive as strings. Anything that isn't a usable number falls back.
const toHours = (value, fallback) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

// Member arrays hold raw uid strings server-side, but the frontend runs docs
// through populateDocsWithUsers first, which swaps each uid for the user object.
// Both shapes reach the resolver, so normalize before comparing.
const includesMember = (list, userId) =>
    Array.isArray(list) && list.some(entry => (entry && typeof entry === 'object' ? entry.uid || entry.id : entry) === userId);

// 'responder' | 'standby' | null — how a member was involved in an incident, for
// pay purposes. `cancelled` members are tracked for stats but are not paid, so
// they resolve to null here.
const resolveIncidentMemberRole = (incident, userId) => {
    if (!incident || !userId) return null;
    if (includesMember(incident.users, userId)) return 'responder';
    if (includesMember(incident.standby, userId)) return 'standby';
    return null;
};

// Paid hours for one member on one incident.
//
//   responder (in `users`):
//        salaryOverrides[uid]                          if usable — ABSOLUTE, incl. 0
//     else Math.max(durations.salary ?? 0, incidentMinimumHours ?? 0)
//
//   standby (in `standby`):
//        salaryOverrides[uid]                          if usable — ABSOLUTE, incl. 0
//     else incidentStandbyHours ?? 2                   — never floored by the minimum
//
// `durations.salary` is DERIVED from the incident's duration, so the minimum is
// both its floor and its default: a 30-minute call and a call with no end time
// at all (Who's Responding supplies a dispatch time only on stood-down calls)
// both pay the minimum. `durations.salaryOverrides[uid]` is a number a human
// typed into the Hours tab for one member — it is honoured exactly and never
// re-floored. Typing 1 means 1; typing 0 means 0. North Shore alone has 18
// explicit zero-overrides, so a read-time clamp here would invent pay.
const resolveIncidentHours = (incident, userId, settings = {}) => {
    const role = resolveIncidentMemberRole(incident, userId);
    if (!role) return 0;

    const {salary, salaryOverrides} = incident.durations || {};

    // Key presence, not truthiness — `salaryOverrides[uid] === 0` is a decision,
    // a missing key is not. A present-but-unusable value (null, '') isn't a
    // decision either, so it falls through rather than zeroing someone's pay.
    if (salaryOverrides && Object.prototype.hasOwnProperty.call(salaryOverrides, userId)) {
        const override = toHours(salaryOverrides[userId], null);
        if (override !== null) return override;
    }

    if (role === 'standby') {
        return toHours(settings.incidentStandbyHours, DEFAULT_STANDBY_HOURS);
    }

    return Math.max(toHours(salary, 0), toHours(settings.incidentMinimumHours, 0));
};

module.exports = {
    DEFAULT_STANDBY_HOURS,
    resolveIncidentMemberRole,
    resolveIncidentHours
};
